import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { lockScroll } from "../utils/scrollLock";
export type DialogVideo = {
  url: string;
  title: string;
  subtitle?: string;
  aspect?: "landscape" | "portrait" | "square";
  poster?: string;
};
type VideoDialogProps = {
  video: DialogVideo | null;
  onClose: () => void;
};
const frames: Record<NonNullable<DialogVideo["aspect"]>, string> = {
  landscape: "aspect-video w-full max-w-[1180px]",
  portrait: "aspect-[9/16] max-h-[74svh]",
  square: "aspect-square max-h-[74svh]"
};

/** Accessible, aspect-aware modal player used for video testimonials. */
export function VideoDialog({
  video,
  onClose
}: VideoDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!video) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], video, [tabindex]:not([tabindex="-1"])'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    const releaseScroll = lockScroll();
    window.addEventListener("keydown", onKeyDown);
    return () => {
      releaseScroll();
      window.removeEventListener("keydown", onKeyDown);
      previousFocus.current?.focus();
    };
  }, [video, onClose]);
  return <AnimatePresence data-id="element-439" data-mp="201rvfieptb2u">
      {video && <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${video.title}, video feedback`} className="fixed inset-0 z-[96] flex flex-col bg-[#040606]/98 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-440" data-mp="24yib44mly4nq">
          <header className="flex min-h-[72px] shrink-0 items-center justify-between gap-6 border-b border-white/10 px-5 md:px-8" data-id="element-441" data-mp="2nwumzy4wlsn0">
            <div className="min-w-0" data-id="element-442" data-mp="2spe8dh9u1s2d">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-443" data-mp="2xnfqnzzfpncs">Video feedback</p>
              <p className="mt-1 truncate text-sm text-white" data-id="element-444" data-mp="32nldn2ureloj">{video.title}</p>
              {video.subtitle && <p className="mt-0.5 truncate text-xs text-white/45" data-id="element-445" data-mp="36nvrf1osmgdy">{video.subtitle}</p>}
            </div>
            <button ref={closeRef} type="button" onClick={onClose} data-cursor="Close" className="flex min-h-11 shrink-0 items-center gap-3 rounded-xl border border-white/15 px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition-colors hover:border-[#54f6c6] hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-446" data-mp="v1agbiog1m9d">
              Close <X className="h-4 w-4" data-id="element-447" data-mp="19py77bnch7sz" />
            </button>
          </header>

          <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-6 md:px-10" data-id="element-448" data-mp="19s16yneaufaw">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className={`relative overflow-hidden rounded-[20px] border border-white/10 bg-black ${frames[video.aspect ?? "landscape"]}`} data-id="element-449" data-mp="14x54sufcmbk2">
              <video src={video.url} poster={video.poster} className="h-full w-full object-contain" controls autoPlay playsInline data-id="element-450" data-mp="1d6ja5vxwavhw" />
            </motion.div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}